import { useEffect, useState } from 'react';
import firestore from '../firestore';
import { CountrySettings, LevelsConfig } from '../context/GlobalContext';
import { UserData } from './auth';
import { type ActiveFinancingOverrides, callAPI, type FinancingOverrideProductType } from './api';

export function GetDev2FANumbers(get_numbers: boolean = false) {
	const [dev2FANumbers, setDev2FANumbers] = useState<{ country: string; phone_number: string }[]>([]);

	useEffect(() => {
		if (!get_numbers) return;
		(async () => {
			try {
				const document = (await firestore.collection('AppSetting').doc('AppSettings').get()).data();
				if (document?.dev_2fa_phone_numbers?.length) {
					setDev2FANumbers(document['dev_2fa_phone_numbers']);
				}
			} catch (error) {
				console.error('Error getting dev2FANumbers:', error);
			}
		})();
	}, [get_numbers]);

	return dev2FANumbers;
}

export type Promotion = {
	label: string;
	description: string;
	payment_options?: { installments: number; factor: number }[];
	initial_percentage?: number;
	approved_credit?: number; // can be Infinity
	imei_tacs?: string[];
	is_active?: boolean;
	min_price?: number;
	stores?: string[];
	excluded_stores?: string[];
	product_types?: string[];
	brands?: string[];
	models?: string[];
	levels?: ('level_1' | 'level_2' | 'level_3' | 'level_4')[];
	promotion_window?: {
		from: {
			seconds: number;
			nanoseconds: number;
		};
		to: {
			seconds: number;
			nanoseconds: number;
		};
	};
};

export function GetTogglablePromotions(
	country: string | null,
	store_email: string | null,
	master_store_email: string | null,
	customer_document_id: string | null,
	userData: UserData | null
) {
	const [result, setResult] = useState<{
		country: string;
		store_email: string;
		master_store_email: string;
		customer_document_id: string | null;
		userData: UserData;
		promotions: Promotion[];
		error: string | null;
	} | null>(null);

	useEffect(() => {
		let cancelled = false;

		setResult(null);
		if (!country || !store_email || !master_store_email || !userData) return;

		(async () => {
			const promos: Promotion[] = [];
			let errorMessage: string | null = null;
			try {
				const [access, accessError] = await callAPI(
					'https://api.krece.app/registration/eligiblepromotions/',
					'POST',
					customer_document_id ? { user_document_id: customer_document_id } : {},
					userData,
					country
				);
				if (
					accessError ||
					!access ||
					!Array.isArray(access.restricted_promotion_codes) ||
					!Array.isArray(access.eligible_promotion_codes) ||
					!access.restricted_promotion_codes.every((code) => typeof code === 'string') ||
					!access.eligible_promotion_codes.every((code) => typeof code === 'string')
				) {
					throw new Error(accessError || 'Promotion eligibility unavailable');
				}

				const restrictedCodes = new Set(access.restricted_promotion_codes);
				const eligibleCodes = new Set(customer_document_id ? access.eligible_promotion_codes : []);
				const doc = (await firestore.collection('Promotions').doc(country).get())?.data() || {};
				for (const promo of Object.keys(doc)) {
					const data = doc[promo] as Promotion;
					if (typeof data?.label !== 'string' || !data.label.trim()) continue;
					const promotionCode = data.label.trim();
					if (restrictedCodes.has(promotionCode) && !eligibleCodes.has(promotionCode)) continue;
					if (
						(data?.stores?.includes('all') || data?.stores?.includes('global') || data?.stores?.includes(store_email) || data?.stores?.includes(master_store_email)) &&
						data?.is_active !== false &&
						(!data?.promotion_window?.from?.seconds || Date.now() / 1000 >= data.promotion_window.from.seconds) &&
						(!data?.promotion_window?.to?.seconds || Date.now() / 1000 < data.promotion_window.to.seconds)
					) {
						if (data?.excluded_stores?.includes(store_email) || data?.excluded_stores?.includes(master_store_email)) continue;
						if (!data?.product_types) data.product_types = ['phone'];
						promos.push(data);
					}
				}
			} catch (error) {
				console.error('Error getting togglable promotions:', error);
				errorMessage = 'No se pudieron validar las promociones. Intente nuevamente.';
			} finally {
				if (!cancelled) {
					setResult({
						country,
						store_email,
						master_store_email,
						customer_document_id,
						userData,
						promotions: errorMessage ? [] : promos,
						error: errorMessage
					});
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [country, store_email, master_store_email, customer_document_id, userData]);

	// Hide the previous result during render, before the new request's effect runs.
	const currentResult =
		result?.country === country &&
		result?.store_email === store_email &&
		result?.master_store_email === master_store_email &&
		result?.customer_document_id === customer_document_id &&
		result?.userData === userData
			? result
			: null;

	return {
		promotions: currentResult?.promotions || [],
		loaded: !!currentResult,
		loadedCustomerDocumentId: currentResult?.customer_document_id ?? null,
		error: currentResult?.error || null
	};
}

export function GetRoutePermissions() {
	type RoutePermissions = {
		admin_portal: {
			route: string;
			countries: string[];
			access: string[];
		}[];
		store_portal: {
			route: string;
			countries: string[];
			access: string[];
		}[];
	};

	const [routePermissions, setRoutePermissions] = useState<RoutePermissions | null>(null);

	useEffect(() => {
		// Use onSnapshot for real-time updates
		const unsubscribe = firestore.collection('AppSetting').doc('RoutePermissions').onSnapshot(
			(doc) => {
				const document = doc.data();
				if (document?.admin_portal || document?.store_portal) {
					setRoutePermissions(document as any);
				}
			},
			(error) => {
				console.error('Error getting route permissions:', error);
			}
		);

		return () => unsubscribe();
	}, []);

	return routePermissions;
}

export function userHasRouteAccess(
	userData: UserData | null | undefined,
	routePermissions: ReturnType<typeof GetRoutePermissions>,
	route: string,
	country: string | undefined | null
) {
	if (!userData || !country) return false;
	const inUserRoutes = !!userData.access_routes?.find(
		({ route: r, countries }) => r === route && countries.includes(country)
	);
	if (inUserRoutes) return true;
	return !!routePermissions?.admin_portal?.find(
		({ route: r, countries, access }) =>
			r === route && countries.includes(country) && access.includes(userData.access_type || '')
	);
}

export function userCanConfigureSellerCommissions(
	userData: UserData | null | undefined,
	routePermissions: ReturnType<typeof GetRoutePermissions>,
	country: string | undefined | null,
	isAdminAccount: boolean
) {
	const internalRoles: UserData['access_type'][] = ['admin', 'admin_finanzas', 'manager', 'atc', 'ata', 'postventa', 'finanzas'];
	const countries = userData?.access_countries || [userData?.country];
	if (!country || !countries.includes(country)) return false;
	const hasDedicatedAccess =
		!!userData?.access_type &&
		internalRoles.includes(userData.access_type) &&
		userHasRouteAccess(userData, routePermissions, 'Comisiones vendedores', country);
	return (
		hasDedicatedAccess ||
		(isAdminAccount && userHasRouteAccess(userData, routePermissions, 'Administrar tiendas', country))
	);
}

export function userCanManageSellerCommissionPayouts(
	userData: UserData | null | undefined,
	routePermissions: ReturnType<typeof GetRoutePermissions>,
	country: string | undefined | null
) {
	const allowedRoles: UserData['access_type'][] = ['admin', 'admin_finanzas', 'manager', 'supervisor', 'finanzas'];
	if (!userData?.access_type || !allowedRoles.includes(userData.access_type)) return false;
	return (
		userHasRouteAccess(userData, routePermissions, 'Pagos a aliados', country) ||
		userHasRouteAccess(userData, routePermissions, 'Administrar tiendas', country)
	);
}

export function GetTestAccounts() {
	const [testAccounts, setTestAccounts] = useState<string[]>([]);

	useEffect(() => {
		(async () => {
			try {
				const document = (await firestore.collection('AppSetting').doc('AppSettings').get()).data();
				if (document?.test_accounts && document?.test_accounts?.length) setTestAccounts(document.test_accounts);
			} catch (error) {
				console.error('Error getting test accounts:', error);
			}
		})();
	}, []);

	return testAccounts;
}

export type UserFinancingOptions = {
	[key in keyof LevelsConfig['base_price_ranges']]?: {
		price_range: {
			min: number;
			max: number;
		};
		down_payment: {
			min: number;
			max: number;
			granularity: number;
		};
		payment_options: {
			factor: number;
			installments: number;
		}[];
	};
};

export function calculate_user_financing_options(
	financing_config: { venezuela: LevelsConfig; peru: LevelsConfig; rd: LevelsConfig },
	country: string,
	level: string
) {
	const base_config = financing_config?.[country as keyof typeof financing_config]?.base_financing_options;
	const base_price_ranges = financing_config?.[country as keyof typeof financing_config]?.base_price_ranges;
	const level_config = financing_config?.[country as keyof typeof financing_config]?.[level as 'level_1'];

	if (!base_config || !level_config || !base_price_ranges) return null;

	const user_financing_options = Object.keys(base_price_ranges).reduce((acc, curr) => {
		const product_type = curr as keyof typeof base_price_ranges;

		const base_price_range = base_price_ranges[product_type];
		const level_price_range = level_config?.additional_price_ranges?.[product_type];

		const base_financing_options = base_config.find((option) => option?.product_types?.includes(product_type));
		const level_financing_options = level_config?.additional_financing_options?.find((option) =>
			option?.product_types?.includes(product_type)
		);

		const payment_options_map = new Map<number, { factor: number; installments: number }>();

		// Add base options first
		for (const option of base_financing_options?.payment_options || []) {
			if (!option.factor || !option.installments) continue;
			payment_options_map.set(option.installments, {
				factor: option.factor,
				installments: option.installments
			});
		}

		// Override with level options if present
		for (const option of level_financing_options?.payment_options || []) {
			if (
				!option.factor ||
				!option.installments ||
				(payment_options_map.has(option.installments) && payment_options_map.get(option.installments)!.factor < option.factor)
			) {
				continue;
			}
			payment_options_map.set(option.installments, {
				factor: option.factor,
				installments: option.installments
			});
		}

		const payment_options = Array.from(payment_options_map.values());
		payment_options.sort((a, b) => a.installments - b.installments);

		acc[product_type] = {
			price_range: {
				min: level_price_range?.min ?? base_price_range.min,
				max: level_price_range?.max ?? base_price_range.max
			},
			down_payment: {
				min: level_financing_options?.down_payment?.min ?? base_financing_options?.down_payment?.min ?? 40,
				max: level_financing_options?.down_payment?.max ?? base_financing_options?.down_payment?.max ?? 50,
				granularity:
					level_financing_options?.down_payment?.granularity ??
					base_financing_options?.down_payment?.granularity ??
					5
			},
			payment_options
		};

		return acc;
	}, {} as UserFinancingOptions);

	return {
		...user_financing_options,
		level_information: level_config.information,
		...(level_config?.credit_line !== undefined ? { credit_line: level_config.credit_line } : {}),
		...(level_config?.credit_line_secondary !== undefined
			? { credit_line_secondary: level_config.credit_line_secondary }
			: {}),
		...(level_config?.recarga_fee !== undefined ? { recarga_fee: level_config.recarga_fee } : {}),
		...(level_config?.personal_loan && { personal_loan: level_config.personal_loan })
	};
}

/**
 * Layers a customer's active financing overrides over calculate_user_financing_options output:
 * each set field replaces the level value for that product (never merged, so it can also be
 * stricter); products the country's Levels don't offer are not created. Promotions, store options
 * and Elite are layered by the sale page, where the customer's own cuotas take priority.
 */
export function apply_financing_overrides(
	financing_options: ReturnType<typeof calculate_user_financing_options>,
	overrides: ActiveFinancingOverrides | null
) {
	if (!financing_options || !overrides) return financing_options;
	type OverridableProduct = keyof UserFinancingOptions & FinancingOverrideProductType;
	const resolved = { ...financing_options };
	for (const product_type of Object.keys(overrides) as OverridableProduct[]) {
		const current = resolved[product_type];
		const product_override = overrides[product_type];
		if (!current || !product_override) continue;
		resolved[product_type] = {
			price_range: product_override.price_range ? { ...product_override.price_range } : current.price_range,
			down_payment: product_override.down_payment ? { ...product_override.down_payment } : current.down_payment,
			payment_options: product_override.payment_options
				? product_override.payment_options.map((option) => ({ ...option }))
				: current.payment_options
		};
	}
	return resolved;
}

/**
 * The customer's financing overrides the backend applies right now, and when they stop applying.
 * Any failure reads as "no override": registration still enforces the customer's real terms, so a
 * sale can never go through on conditions the backend doesn't accept.
 */
export async function get_customer_financing_overrides(user_email: string, userData: UserData | null, country: string | null) {
	// Never hold the sale form on this lookup
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), 8000);
	const [data, error] = await callAPI(
		'https://api.krece.app/users/financing-overrides/',
		'GET',
		{ user_email },
		userData,
		country,
		true,
		false,
		controller.signal
	);
	clearTimeout(timeout);
	const active = data?.active_financing_overrides;
	if (error || !active || !Object.keys(active).length) return null;
	const expires_at_ms = data?.financing_overrides?.expires_at ? Date.parse(data.financing_overrides.expires_at) : NaN;
	return { active, expires_at_ms: Number.isFinite(expires_at_ms) ? expires_at_ms : null };
}

export function calculate_permissions_blacklist(userData: UserData | null) {
	if (!userData?.email) return null;

	if (
		!userData?.master_store?.email ||
		userData?.email === userData?.master_store.email ||
		!userData?.master_store?.seller_permissions_blacklist
	) {
		return [];
	}

	const blacklist = new Set<string>([
		...(userData?.master_store?.seller_permissions_blacklist?.all_sellers || []),
		...(userData?.master_store?.seller_permissions_blacklist?.[userData?.email] || [])
	]);

	return Array.from(blacklist || []);
}

export function calculate_permissions_whitelist(userData: UserData | null, permissionsBlacklist: string[]) {
	if (!userData?.email) return [];

	if (
		!userData?.master_store?.email ||
		userData?.email === userData?.master_store.email ||
		!userData?.master_store?.seller_permissions_whitelist
	) {
		return [];
	}

	const whitelist = new Set<string>([
		...(userData?.master_store?.seller_permissions_whitelist?.all_sellers || []),
		...(userData?.master_store?.seller_permissions_whitelist?.[userData?.email] || [])
	]);

	// Blacklist always takes priority
	for (const item of permissionsBlacklist) {
		whitelist.delete(item);
	}

	return Array.from(whitelist);
}

export type DelinquencyStatus = 'GOOD' | 'NEUTRAL' | 'BAD';
export function calculate_store_delinquency(
	userData: UserData | null,
	CountrySettings: CountrySettings | null
): null | DelinquencyStatus {
	if (
		!userData?.master_store?.email ||
		!userData?.master_store?.delinquency ||
		!CountrySettings?.delinquency_ranges?.low ||
		!CountrySettings?.delinquency_ranges?.high
	)
		return null;

	const document = userData?.master_store;

	const delinquency = document.delinquency!;
	const delinquency_ranges = CountrySettings.delinquency_ranges!;

	if (delinquency <= delinquency_ranges.low!) {
		return 'GOOD';
	} else if (delinquency > delinquency_ranges.low! && delinquency < delinquency_ranges.high!) {
		return 'NEUTRAL';
	} else {
		return 'BAD';
	}
}

export function useBCVExchangeRate(country: string) {
    const [exchangeRate, setExchangeRate] = useState<number>(1);

    useEffect(() => {
        if (country !== 'venezuela') return;

        (async () => {
            try {
                const docRef = firestore.collection('DollarPrice').doc('dollarPrice');
                const docSnap = await docRef.get();

                if (docSnap.exists) {
                    setExchangeRate(Number.parseFloat(docSnap.data()?.["bcv"]) || 1);
                }
            } catch (error) {
                console.error("Error fetching BCV exchange rate:", error);
            }
        })();
    }, [country]);

    return exchangeRate;
}